import { useMemo, useState, useEffect } from 'react';
import { Stack, Tooltip } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SMS } from 'domains/content/types';
import Button from 'domains/core/components/Button';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText/ContentStatusText';

type Props = {
    hasInvalidAutoResponses?: boolean;
    isLoading: boolean;
    onSaveAndExit: (status?: ContentStatus) => void;
    smsData?: SMS;
};

const SaveAndExitButton = ({ hasInvalidAutoResponses, isLoading, onSaveAndExit, smsData }: Props) => {
    const { contentDraft: hasContentDraft } = useFlags();
    const [currentAction, setCurrentAction] = useState<'saving' | 'publishing' | null>(null);
    const isMessageInvalidLength = smsData?.content?.length < 3 || smsData?.content?.length > 1600;

    // Reset currentAction when loading is complete
    useEffect(() => {
        if (!isLoading) {
            setCurrentAction(null);
        }
    }, [isLoading]);

    const handleSaveAndExitClick = () => {
        setCurrentAction('saving');
        onSaveAndExit(ContentStatus.DRAFT);
    };

    const handlePublishClick = () => {
        setCurrentAction('publishing');
        onSaveAndExit(ContentStatus.PUBLISHED);
    };

    const tooltipTitle = useMemo(() => {
        let tooltipTitle = '';
        if (isLoading && currentAction === 'publishing') {
            tooltipTitle = tooltipTitle + 'Content is publishing. ';
        } else if (isLoading && currentAction === 'saving') {
            tooltipTitle = tooltipTitle + 'Content is saving. ';
        } else if (isLoading) {
            tooltipTitle = tooltipTitle + 'Content is loading. ';
        }
        if (isMessageInvalidLength) {
            tooltipTitle = tooltipTitle + 'Message must be between 3 and 1600 characters. ';
        }
        if (hasInvalidAutoResponses) {
            tooltipTitle = tooltipTitle + 'Content cannot be saved with invalid autoresponses. ';
        }
        return tooltipTitle;
    }, [hasInvalidAutoResponses, isLoading, isMessageInvalidLength, currentAction]);

    return (
        <Tooltip title={tooltipTitle}>
            <Stack direction="row" alignItems="center" columnGap={1} ml={1}>
                <Button
                    disabled={isLoading || hasInvalidAutoResponses || isMessageInvalidLength}
                    onClick={handleSaveAndExitClick}
                    variant="outlined"
                    data-testid="save-exit-button"
                    role="button"
                >
                    Save & Exit
                </Button>
                {hasContentDraft && (
                    <Button
                        disabled={isLoading || hasInvalidAutoResponses || isMessageInvalidLength}
                        onClick={handlePublishClick}
                        variant="contained"
                        data-testid="publish-button"
                        role="button"
                    >
                        Publish
                    </Button>
                )}
            </Stack>
        </Tooltip>
    );
};

export default SaveAndExitButton;
